import { bankAccountSchema, PaymentMethod, paymentMethodOptions } from "@nano-portal/customers"
import { objectKeys, objectKeysToZodEnum } from "@nano-portal/shared"
import { z } from "zod"
import { Product } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { assertUnreachable } from "../../../../../utils/types"
import { useSafeLocalStorage } from "../../../../../utils/localStorage"

export const paymentMethodSchema = objectKeysToZodEnum(paymentMethodOptions, {
  errorMap: () => ({ message: "Tato hodnota nesmí být prázdná" }),
})

const consumptionPaymentFormSchema = z.object({
  advancePaymentMethod: paymentMethodSchema,
  advancePaymentBankAccount: bankAccountSchema,
  partnerCode: z.string().optional(),
  message: z.string().optional(),
})

const productionPaymentFormSchema = z.object({
  productionPaymentBankAccount: bankAccountSchema,
  partnerCode: z.string(),
  message: z.string().optional(),
})

const productionAndConsumptionPaymentFormSchema = z.object({
  advancePaymentMethod: paymentMethodSchema,
  advancePaymentBankAccount: bankAccountSchema,

  productionPaymentBankAccount: bankAccountSchema.optional(),

  partnerCode: z.string(),
  message: z.string().optional(),
})

export type ConsumptionPaymentFormValues = z.infer<typeof consumptionPaymentFormSchema>
export type ProductionPaymentFormValues = z.infer<typeof productionPaymentFormSchema>
export type ProductionAndConsumptionPaymentFormValues = z.infer<typeof productionAndConsumptionPaymentFormSchema>

export type PaymentFormValues =
  | ConsumptionPaymentFormValues
  | ProductionPaymentFormValues
  | ProductionAndConsumptionPaymentFormValues

export function hasConsumptionBankAccount(
  paymentFormValues: PaymentFormValues
): paymentFormValues is ConsumptionPaymentFormValues {
  return "advancePaymentMethod" in paymentFormValues
}

export function hasProductionBankAccount(
  paymentFormValues: PaymentFormValues
): paymentFormValues is ProductionPaymentFormValues {
  return "productionPaymentBankAccount" in paymentFormValues
}

export function hasProductionAndConsumptionBankAccounts(
  paymentFormValues: PaymentFormValues
): paymentFormValues is ProductionAndConsumptionPaymentFormValues {
  return "advancePaymentMethod" in paymentFormValues && "productionPaymentBankAccount" in paymentFormValues
}

export function usePaymentFormSchema<
  P extends Product,
  R = P extends "consumption"
    ? typeof consumptionPaymentFormSchema
    : P extends "production"
    ? typeof productionPaymentFormSchema
    : typeof productionAndConsumptionPaymentFormSchema
>(product: P): R {
  switch (product) {
    case "consumption":
      return consumptionPaymentFormSchema as R
    case "production":
      return productionPaymentFormSchema as R
    case "productionAndConsumption":
      return productionAndConsumptionPaymentFormSchema as R
    default:
      return assertUnreachable(product)
  }
}

export function useStoredPaymentFormValues(product: Product) {
  const schema = usePaymentFormSchema(product)
  return useSafeLocalStorage<PaymentFormValues>({ key: "acquisition-form-payment", schema })
}

function usePaymentMethodLabelResolver() {
  return (paymentMethod: PaymentMethod) => {
    switch (paymentMethod) {
      case "BANK_ORDER":
        return paymentMethodOptions.BANK_ORDER
      case "BANK_ENCASHMENT":
        return "Inkaso (preferovaný způsob úhrady)"
      default:
        return assertUnreachable(paymentMethod)
    }
  }
}

export function usePaymentMethodSelectOptions() {
  const resolvePaymentMethodLabel = usePaymentMethodLabelResolver()
  return (
    objectKeys(paymentMethodOptions)
      .sort((a, b) => (b < a ? 1 : -1))
      // eslint-disable-next-line unicorn/no-array-reduce
      .reduce((acc, paymentMethod) => {
        acc[paymentMethod] = resolvePaymentMethodLabel(paymentMethod)
        return acc
      }, {} as Record<PaymentMethod, string>)
  )
}
